/**
 * DateInput — a Dialog-safe date picker.
 *
 * Fixes a known interaction bug with native `<input type="date">` inside a
 * Radix Dialog: clicking inside the browser's native calendar popup is
 * sometimes captured as an "outside click" and dismisses the parent Dialog,
 * kicking the user back to step 1.
 *
 * This component gives users BOTH:
 *   1. A free-text input (YYYY-MM-DD) they can type into directly.
 *   2. A small calendar button that opens a Radix Popover with the shadcn
 *      Calendar — same primitive family as Dialog, so outside-click handling
 *      plays nicely together and the Dialog stays open.
 *
 * API: <DateInput value={iso} onChange={(iso) => ...} placeholder? data-testid? />
 *   - value / onChange always use `YYYY-MM-DD` (empty string when cleared).
 *   - Manual typing is parsed loosely; invalid dates don't mutate state.
 */
import React, { useState, useMemo } from 'react';
import { Calendar as CalendarIcon } from 'iconoir-react';
import { Calendar } from './calendar';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Input } from './input';
import { Button } from './button';

const ISO_RE = /^\d{4}-\d{2}-\d{2}$/;

const toDate = (iso) => {
  if (!iso || !ISO_RE.test(iso)) return undefined;
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime()) ? undefined : d;
};

const toIso = (d) => {
  if (!d) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export default function DateInput({
  value,
  onChange,
  placeholder = 'YYYY-MM-DD',
  className = 'input-field h-8 text-xs font-mono',
  'data-testid': testid,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(value || '');

  // Keep local text in sync when parent value changes externally.
  React.useEffect(() => { setText(value || ''); }, [value]);

  const selectedDate = useMemo(() => toDate(value), [value]);

  const commitText = (next) => {
    setText(next);
    if (next === '') { onChange(''); return; }
    if (ISO_RE.test(next) && toDate(next)) onChange(next);
    // else leave parent value untouched until the user fixes the format
  };

  return (
    <div className="flex items-stretch gap-1">
      <Input
        type="text"
        inputMode="numeric"
        value={text}
        onChange={(e) => commitText(e.target.value)}
        placeholder={placeholder}
        className={`${className} flex-1`}
        disabled={disabled}
        data-testid={testid}
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="icon"
            disabled={disabled}
            aria-label="Open date picker"
            className="h-8 w-8 shrink-0 border-border bg-white hover:bg-page"
            data-testid={testid ? `${testid}-picker` : undefined}
          >
            <CalendarIcon className="w-3.5 h-3.5 text-muted" strokeWidth={1.75} />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="end"
          side="bottom"
          className="w-auto p-0 z-[60]"
          // Keep the parent Dialog from closing when the user clicks inside
          // the popover overlay that Radix renders underneath.
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={(d) => {
              const iso = toIso(d);
              setText(iso);
              onChange(iso);
              setOpen(false);
            }}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
